import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetStoreTransfersQuery, useDeleteStoreTransferMutation } from '../../slices/storeTransfersApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from 'xlsx';

const StoreTransferListScreen = () => {
  // States
  const [filter, setFilter] = useState('');
  const [refineFilter, setRefineFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch data
  const { data, isLoading, error, refetch } = useGetStoreTransfersQuery(pageNumber);
  const [deleteStoreTransfer, { isLoading: loadingDelete }] = useDeleteStoreTransferMutation();

  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeTransfer?')) {
      try {
        await deleteStoreTransfer(id).unwrap();
        toast.success('StoreTransfer deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete storeTransfer.');
      }
    }
  };

  // ORIGINAL filtering logic (unchanged)
  const filterStoreTransfers = () => {
    if (!data?.storeTransfers) return [];

    return data.storeTransfers.filter((storeTransfer) => {
      const storeTransferDate = new Date(storeTransfer?.date || new Date());
      const isWithinDateRange =
        (!startDate || storeTransferDate >= new Date(startDate)) &&
        (!endDate || storeTransferDate <= new Date(endDate));

      const lowerFilter = filter.toLowerCase();

      return (
        isWithinDateRange &&
        (
          storeTransfer?.note?.toLowerCase().includes(lowerFilter) ||
          storeTransfer?.customerDetails?.name?.toLowerCase().includes(lowerFilter) ||
          storeTransfer?.storeTransferItems?.some((item) =>
            item?.name?.toLowerCase().includes(lowerFilter)
          )
        )
      );
    });
  };

  // SECOND FILTER: refine by product + note + patient
  const refineStoreTransferItems = (storeTransfer) => {
    const lower = refineFilter.toLowerCase();

    const matchesNote = storeTransfer.note?.toLowerCase().includes(lower);
    const matchesPatient = storeTransfer.customerDetails?.name?.toLowerCase().includes(lower);

    const matchesItems = storeTransfer.storeTransferItems.filter((i) =>
      i.name.toLowerCase().includes(lower)
    );

    if (matchesNote || matchesPatient) return storeTransfer.storeTransferItems;

    return matchesItems;
  };

  // Totals
  const calculateTotalStoreTransfers = () => {
    return filterStoreTransfers().reduce((total, storeTransfer) => {
      const refinedItems = refineStoreTransferItems(storeTransfer);

      const storeTransferTotal = refinedItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );

      return total + storeTransferTotal;
    }, 0);
  };

  const calculateTotalQty = () => {
    return filterStoreTransfers().reduce((totalQty, storeTransfer) => {
      const refinedItems = refineStoreTransferItems(storeTransfer);

      return totalQty + refinedItems.reduce((sum, item) => sum + item.qty, 0);
    }, 0);
  };

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filterStoreTransfers().flatMap((storeTransfer) => {
      const refinedItems = refineStoreTransferItems(storeTransfer);

      return refinedItems.map((i) => ({
        Date: new Date(storeTransfer.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: storeTransfer.note || 'N/A',
        Patient: storeTransfer.customerDetails?.name || 'N/A'
      }));
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'StoreTransfers');
    XLSX.writeFile(wb, 'StoreTransfers_Report.xlsx');
  };

  // Pagination
  const paginationHandler = (page) => setPageNumber(page);

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>StoreTransfers</h1>

          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>

          <Link to='/storeAdmin/storeTransfersearchReplace' className='btn btn-light my-3'>
            Search
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by product, note, or patient"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product, note, or patient"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>

        <Col md={6} className="text-end">
          <h4>Total StoreTransfers: ${calculateTotalStoreTransfers().toFixed(2)}</h4>
          <h4>Total Qty: {calculateTotalQty().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : filterStoreTransfers().length === 0 ? (
        <Message>No storeTransfers match your criteria.</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>PATIENT</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterStoreTransfers().map((storeTransfer) => {
                const refinedItems = refineStoreTransferItems(storeTransfer);

                return refinedItems.map((item) => (
                  <tr key={`${storeTransfer?._id}-${item?.name}`}>
                    <td>{new Date(storeTransfer?.date).toLocaleDateString()}</td>
                    <td>{item?.name}</td>
                    <td>{item?.qty}</td>
                    <td>${(item?.qty * item?.price).toFixed(2)}</td>
                    <td>{storeTransfer?.note || 'N/A'}</td>
                    <td>{storeTransfer?.customerDetails?.name || 'N/A'}</td>
                    <td>
                      {item?.images?.[0] ? (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      ) : (
                        'No image'
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/storeTransfer/${storeTransfer?._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>

                      <Button
                        as={Link}
                        to={`/storeTransfer/${storeTransfer?._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        as={Link}
                        to={`/storeTransfer/${storeTransfer?._id}/copy`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Copy
                      </Button>

                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(storeTransfer?._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StoreTransferListScreen;
